<i18n>
{
	"en": {
		"customer": "Customer",
		"customerMissing": "Customer missing",
		"to": "To",
		"toMissing": "Delivery party missing",
		"from": "From",
		"fromMissing": "Despatch party missing",
		"moreCustomers": "and 1 more customer | and {count} more customers",
		"moreDespatchParties": "and 1 more pickup location | and {count} more pickup locations",
		"moreDeliveryParties": "and 1 more delivery location | and {count} more delivery locations"
	},
	"no": {
		"customer": "Kunde",
		"customerMissing": "Kunde mangler",
		"to": "Til",
		"toMissing": "Mottaker mangler",
		"from": "Fra",
		"fromMissing": "Henteaddresse mangler",
		"moreCustomers": "og 1 kunde til | og {count} flere kunder",
		"moreDespatchParties": "og 1 henteaddresse til | og {count} flere henteaddresser",
		"moreDeliveryParties": "og 1 leveringsaddresse til | og {count} flere leveringsaddresser"
	}
}
</i18n>
<template>
	<div v-if="!singleLocation" class="map-modal-container">
		<div class="map-modal-trip-info-container" >
			<div v-for="data in firstUniqueCustomers" :key="data.id" class="map-modal-info">
				<div class="map-modal-info-labels">
					{{$t('customer')}} <template v-if="data.count > 1">({{data.count}})</template>
				</div>
				<div class="map-modal-info-data-item" :class="{missing: !data.customerName}">
					{{ data.customerName || $t('customerMissing') }}
				</div>
			</div>
			<div
				v-if="moreCustomersCount > 0"
				class="map-modal-info"
			>
				{{ $tc('moreCustomers',moreCustomersCount)}}
			</div>
		</div>
		<div class="map-modal-trip-info-container" >
			<div v-for="data in firstUniqueDespatchParties" :key="data.id" class="map-modal-info">
				<div class="map-modal-info-labels">
					{{$t('from')}} <template v-if="data.count > 1">({{data.count}})</template>
				</div>
				<div v-if="!data.despatchParty" class="map-modal-info-data-item missing">
					{{ $t('fromMissing') }}
				</div>
				<div v-else class="map-modal-info-data-item">
					<div class="item-main-text">{{ data.despatchParty }}</div>
					<div class="item-sub-text">{{ data.postalPlace }} {{ data.postalCode }}</div>
				</div>
			</div>
			<div
				v-if="moreDespatchPartiesCount > 0"
				class="map-modal-info"
			>
				{{ $tc('moreDespatchParties',moreDespatchPartiesCount)}}
			</div>
		</div>
		<div class="map-modal-trip-info-container" >
			<div v-for="data in firstUniqueDeliveryParties" :key="data.id" class="map-modal-info">
				<div class="map-modal-info-labels">
					{{$t('to')}} <template v-if="data.count > 1">({{data.count}})</template>
				</div>
				<div v-if="!data.deliveryParty" class="map-modal-info-data-item missing">
					{{ $t('toMissing') }}
				</div>
				<div v-else class="map-modal-info-data-item">
					<div class="item-main-text">{{ data.deliveryParty }}</div>
					<div class="item-sub-text">{{ data.postalPlace }} {{ data.postalCode }}</div>
				</div>
			</div>
			<div
				v-if="moreDeliveryPartiesCount > 0"
				class="map-modal-info"
			>
				{{ $tc('moreDeliveryParties',moreDeliveryPartiesCount)}}
			</div>
		</div>
	</div>
	<div v-else class="map-modal-container">
		<div class="map-modal-trip-info-container" >
			<div class="map-modal-info">
				<div class="map-modal-info-labels">
					{{$t('customer')}}
				</div>
				<div class="map-modal-info-data-item" :class="{missing: !customers[0].customerName}">
					{{customers[0].customerName}}
				</div>
			</div>
			<div class="map-modal-info">
				<div class="map-modal-info-labels">
					{{$t('from')}}
				</div>
				<div class="map-modal-info-data-item" :class="{missing: !despatchParties[0].despatchParty}">
					{{despatchParties[0].despatchParty}}
				</div>
			</div>
			<div class="map-modal-info">
				<div class="map-modal-info-labels">
					{{$t('to')}}
				</div>
				<div class="map-modal-info-data-item" :class="{missing: !deliveryParties[0].deliveryParty}">
					{{deliveryParties[0].deliveryParty}}
				</div>
			</div>
		</div>
	</div>
</template>
<script>
export default {
	name: 'MapModalClusterTooltip',
	DISPLAY_COUNT: 3,
	props: {
		orders: {
			type: Array,
			required: true
		},
	},
	data() {
		return {
			customers: [],
			despatchParties: [],
			deliveryParties: []
		};
	},
	computed: {
		filteredOrders() {
			return this.orders.map( x => ({
				customerName: x.customer && x.customer.name ? x.customer.name : '',
				despatchParty: x.despatchParty && x.despatchParty.name ? x.despatchParty.name : '',
				fullDespatchParty: x.despatchParty || {},
				deliveryParty: x.deliveryParty && x.deliveryParty.name ? x.deliveryParty.name : '',
				fullDeliveryParty: x.deliveryParty || {},
			}));
		},
		firstUniqueCustomers() {
			return this.customers.slice(0,this.$options.DISPLAY_COUNT);
		},
		moreCustomersCount() {
			return this.customers.length - this.$options.DISPLAY_COUNT;
		},
		firstUniqueDespatchParties() {
			return this.despatchParties.slice(0,this.$options.DISPLAY_COUNT);
		},
		moreDespatchPartiesCount() {
			return this.despatchParties.length - this.$options.DISPLAY_COUNT;
		},
		firstUniqueDeliveryParties() {
			return this.deliveryParties.slice(0,this.$options.DISPLAY_COUNT);
		},
		moreDeliveryPartiesCount() {
			return this.deliveryParties.length - this.$options.DISPLAY_COUNT;
		},
		singleLocation() {
			return this.customers.length === 1 && this.despatchParties.length === 1 && this.deliveryParties.length === 1;
		}
	},
	watch: {
		orders: {
			immediate: true,
			handler() {
				this.processOrderData();
			}
		}
	},
	methods: {
		processOrderData() {
			let customers = {};
			let despatchParties = {};
			let deliveryParties = {};

			for (let order of this.filteredOrders) {
				let customerName = order.customerName.toLowerCase();
				if (customers.hasOwnProperty(customerName)) {
					customers[customerName].count++;
				} else {
					customers[customerName] = {
						customerName: order.customerName,
						count: 1,
						id: customerName
					};
				}

				let despatchParty = order.despatchParty.toLowerCase();
				if (despatchParties.hasOwnProperty(despatchParty)) {
					despatchParties[despatchParty].count++;
				} else {
					despatchParties[despatchParty] = {
						despatchParty: order.despatchParty,
						postalCode: order.fullDespatchParty.postalCode,
						postalPlace: order.fullDespatchParty.postalPlace,
						count: 1,
						id: despatchParty
					};
				}

				let deliveryParty = order.deliveryParty.toLowerCase();
				if (deliveryParties.hasOwnProperty(deliveryParty)) {
					deliveryParties[deliveryParty].count++;
				} else {
					deliveryParties[deliveryParty] = {
						deliveryParty: order.deliveryParty,
						postalCode: order.fullDeliveryParty.postalCode,
						postalPlace: order.fullDeliveryParty.postalPlace,
						count: 1,
						id: deliveryParty
					};
				}
			}

			this.customers = Object.values(customers).sort((a,b) => b.count-a.count);
			this.despatchParties = Object.values(despatchParties).sort((a,b) => b.count-a.count);
			this.deliveryParties = Object.values(deliveryParties).sort((a,b) => b.count-a.count);
		}
	}
};
</script>

<style lang="scss" scoped>
@import '~@/components/evergreen/scss/colors';
@import '~@/components/evergreen/scss/text';

.map-modal-container {
	padding: 8px 10px 8px 10px;
}
.map-modal-trip-info-container{
	display: flex;
	flex-direction: column;
	padding: 3px 0;
}
.map-modal-info {
	display: flex;
	flex-direction: row;
	@extend %text-xsmall;
	.map-modal-info-data-item, .map-modal-info-labels {
		flex: 1;
		line-height: 16px;
		color: get-color($text, 'dark');
	}
	.map-modal-info-labels {
		flex-grow: 1;
	}
	.map-modal-info-data-item {
		@extend %text-small;
		font-weight: 600;
		flex-grow: 3;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
		&.missing {
			font-style: italic;
			color: get-color($text, 'light');
		}
		.item-sub-text {
			height:16px;
			font-size: 10px;
			font-weight: normal;
			font-style: normal;
			font-stretch: normal;
			line-height: 1.6;
			letter-spacing: normal;
			overflow: hidden;
			white-space: nowrap;
		}
	}
}
</style>
